import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { IoCreateOutline } from 'react-icons/io5';
import AddManagedAccountModal from './AddManagedAccountModal';
import CareOverviewTab from './CareOverviewTab';
import CareInsightsTab from './CareInsightsTab';
import CareRemindersTab from './CareRemindersTab';
import CareAlertsTab from './CareAlertsTab';
import CareCaregiversTab from './CareCaregiversTab';
import './CareDashboard.css';

/**
 * Unified Care dashboard — slide-in panel that replaces the old heart
 * popover + breadcrumb pills + standalone modals. Left rail switches
 * between managed accounts; the main area shows Overview / Reminders /
 * Alerts for the active account.
 *
 * Props:
 *   token, api
 *   initialAccount  — the account to open on (from the heart icon click)
 *   initialTab      — 'overview' | 'reminders' | 'alerts' (default overview)
 *   onClose         — close the dashboard
 *   onViewMemories(account) — hand off to the main memory view
 */
export default function CareDashboard({ token, api, initialAccount, initialTab = 'overview', onClose, onViewMemories }) {
  const [accounts, setAccounts] = useState(initialAccount && initialAccount.id ? [initialAccount] : []);
  const [activeId, setActiveId] = useState(initialAccount?.id || null);
  const [tab, setTab] = useState(initialTab);
  const [addOpen, setAddOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [calling, setCalling] = useState(false);
  const [openAlertCount, setOpenAlertCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${api}/api/managed-accounts`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      const list = data.managed_accounts || [];
      setAccounts(list);
      // Default to the first account only if none is selected yet.
      setActiveId(prev => prev || (list[0] ? list[0].id : null));
    } catch { /* keep initialAccount */ } finally { setLoaded(true); }
  }, [api, token]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Re-sync when the dashboard is re-targeted while already open — the crisis
  // banner sits above the dashboard (higher z-index) and can reopen it on a
  // different account/tab without unmounting this component, so the mount-only
  // useState initializers above wouldn't otherwise pick up the new props.
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  useEffect(() => { if (initialAccount?.id) setActiveId(initialAccount.id); }, [initialAccount?.id]);

  const active = accounts.find(a => a.id === activeId) || (initialAccount?.id ? initialAccount : null);

  // Refresh the alert badge whenever the active account changes.
  const refreshAlertBadge = useCallback(async () => {
    if (!active?.id) return;  // skip the sentinel {id:null} window (avoids /null/crisis-events 403)
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(active.id)}/crisis-events`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setOpenAlertCount((d.crisis_events || []).filter(e => !e.resolved_at).length); }
    } catch { /* ignore */ }
  }, [api, token, active?.id]);
  useEffect(() => { refreshAlertBadge(); }, [refreshAlertBadge]);

  const [myInvites, setMyInvites] = useState([]);
  const fetchMyInvites = useCallback(async () => {
    try {
      const r = await fetch(`${api}/api/caregiver-invites/pending`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setMyInvites(d.pending_invites || []); }
    } catch { /* ignore */ }
  }, [api, token]);
  useEffect(() => { fetchMyInvites(); }, [fetchMyInvites]);

  const acceptInvite = async (inviteId) => {
    try {
      const r = await fetch(`${api}/api/caregiver-invites/${encodeURIComponent(inviteId)}/accept`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({}) });
      if (!r.ok) return;
      await fetchMyInvites();
      await fetchAccounts();   // the newly-accepted account now appears in the rail
    } catch { /* ignore */ }
  };

  const callNow = async () => {
    if (!active?.id) return;
    setCalling(true);
    try {
      const res = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(active.id)}/call`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); alert(`Couldn't place call: ${d.detail || res.status}`); }
      else alert(`Calling ${active.name} now…`);
    } catch (e) { alert(`Couldn't place call: ${e.message}`); }
    finally { setCalling(false); }
  };

  const handleCreated = (acct) => {
    setAddOpen(false);
    setAccounts(prev => [acct, ...prev]);
    setActiveId(acct.id);
    setTab('overview');
  };

  // Keep the rail/account list in sync when the companion voice changes, so
  // re-selecting the account doesn't re-mount the tab from a stale prop and
  // make the change look like it reverted.
  const handleVoiceChanged = useCallback((accountId, newVoiceId) => {
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, companion_voice_id: newVoiceId } : a));
  }, []);

  const handleSaved = async (result) => {
    setEditingAccount(null);
    if (result && result.removed) {
      // Clear the removed account FIRST so fetchAccounts' `prev || first`
      // falls through to a remaining account instead of keeping the removed id.
      setActiveId(prev => (prev === result.id ? null : prev));
      await fetchAccounts();
    } else {
      await fetchAccounts();
    }
  };

  return ReactDOM.createPortal(
    <div className="care-dash-overlay" onClick={onClose}>
      <div className="care-dash" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Care dashboard">
        {/* Account rail */}
        <div className="care-dash-rail">
          <div className="care-dash-rail-title">Care</div>
          {accounts.map(a => (
            <div key={a.id} className={`care-rail-item ${a.id === activeId ? 'active' : ''}`}
              role="button" tabIndex={0}
              onClick={() => { setActiveId(a.id); setTab('overview'); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveId(a.id); setTab('overview'); } }}>
              <div className="care-avatar">{(a.name || '?').slice(0, 1).toUpperCase()}</div>
              <div style={{ minWidth: 0 }}>
                <div className="care-rail-name">{a.name}</div>
                <div className="care-rail-sub">{a.relationship ? `your ${a.relationship}` : (a.condition_label || 'managed')}</div>
              </div>
            </div>
          ))}
          <button className="care-rail-add" onClick={() => setAddOpen(true)}>+ Add account</button>
        </div>

        {/* Main */}
        <div className="care-dash-main">
          {myInvites.length > 0 && (
            <div style={{ padding: '12px 22px', background: 'rgba(232,199,123,0.12)', borderBottom: '1px solid rgba(232,199,123,0.3)' }}>
              {myInvites.map((iv) => (
                <div key={iv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(254,252,246,0.9)' }}>
                  <span style={{ flex: 1 }}>
                    {iv.invited_by_name || 'A family member'} invited you to help care for <strong>{iv.pwd_name}</strong>.
                  </span>
                  <button className="care-btn primary" style={{ padding: '6px 14px' }} onClick={() => acceptInvite(iv.id)}>Accept</button>
                </div>
              ))}
            </div>
          )}
          {active ? (
            <>
              <div className="care-dash-header">
                <div className="care-avatar lg">{(active.name || '?').slice(0, 1).toUpperCase()}</div>
                <div className="care-dash-header-info">
                  <div className="care-dash-title">
                    {active.name}
                    <button
                      className="care-edit-pencil"
                      onClick={() => setEditingAccount(active)}
                      aria-label={`Edit ${active.name}'s details (name, phone number, companion)`}
                      type="button"
                    >
                      <IoCreateOutline /> Edit
                    </button>
                  </div>
                  <div className="care-dash-subtitle">
                    {active.condition_label || 'managed'}{active.relationship ? ` · your ${active.relationship}` : ''}
                  </div>
                </div>
                <button className="care-dash-close" onClick={onClose} aria-label="Close dashboard">×</button>
              </div>

              <div className="care-dash-tabs" role="tablist">
                <button className={`care-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')} role="tab" aria-selected={tab === 'overview'}>Overview</button>
                <button className={`care-tab ${tab === 'insights' ? 'active' : ''}`} onClick={() => setTab('insights')} role="tab" aria-selected={tab === 'insights'}>Insights</button>
                <button className={`care-tab ${tab === 'reminders' ? 'active' : ''}`} onClick={() => setTab('reminders')} role="tab" aria-selected={tab === 'reminders'}>Reminders</button>
                <button className={`care-tab ${tab === 'alerts' ? 'active' : ''}`} onClick={() => setTab('alerts')} role="tab" aria-selected={tab === 'alerts'}>
                  Alerts{openAlertCount > 0 && <span className="care-tab-badge">{openAlertCount}</span>}
                </button>
                <button className={`care-tab ${tab === 'caregivers' ? 'active' : ''}`} onClick={() => setTab('caregivers')} role="tab" aria-selected={tab === 'caregivers'}>Caregivers</button>
              </div>

              <div className="care-dash-body">
                {tab === 'overview' && (
                  <CareOverviewTab
                    account={active} token={token} api={api} calling={calling}
                    onCallNow={callNow}
                    onVoiceChanged={handleVoiceChanged}
                    onViewMemories={onViewMemories ? () => { if (active?.id) { onViewMemories(active); onClose?.(); } } : undefined}
                  />
                )}
                {tab === 'insights' && <CareInsightsTab account={active} api={api} token={token} />}
                {tab === 'reminders' && <CareRemindersTab account={active} token={token} api={api} />}
                {tab === 'alerts' && <CareAlertsTab account={active} token={token} api={api} onChanged={refreshAlertBadge} />}
                {tab === 'caregivers' && <CareCaregiversTab account={active} token={token} api={api} />}
              </div>
            </>
          ) : loaded ? (
            <div className="care-empty-state" style={{ margin: 'auto' }}>
              You're not managing any accounts yet.<br />
              <button className="care-btn primary" style={{ marginTop: 12 }} onClick={() => setAddOpen(true)}>+ Add managed account</button>
            </div>
          ) : null}
        </div>
      </div>

      {addOpen && (
        <AddManagedAccountModal token={token} api={api} onClose={() => setAddOpen(false)} onCreated={handleCreated} />
      )}
      {editingAccount && (
        <AddManagedAccountModal
          token={token}
          api={api}
          account={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSaved={handleSaved}
        />
      )}
    </div>,
    document.body,
  );
}
